import { z } from "zod";
/**
 * Validators for the workflow execution layer: run lifecycle, step events,
 * and the inputs the agent-facing workflow MCP tools accept.
 *
 * The workflow definition itself (nodes/edges) lives in
 * ./agent-workflow.ts — these schemas describe *runs* over those graphs.
 */
export const WORKFLOW_RUN_STATUSES = [
    "running",
    "awaiting",
    "completed",
    "failed",
    "cancelled",
];
export const WORKFLOW_STEP_STATUSES = [
    "running",
    "awaiting",
    "completed",
    "failed",
    "skipped",
];
export const WORKFLOW_AWAIT_TYPES = [
    "teams_card",
    "webhook",
    "schedule",
    "email_reply",
];
// --------------------------------------------------------------------
// Human-facing: starting / cancelling a run
// --------------------------------------------------------------------
export const startWorkflowRunSchema = z.object({
    // Optional override — by default the platform picks the first agent node's
    // agentId. Set this when a workflow has multiple agent nodes or none
    // (rare; usually means "run as Codie" or similar).
    assignedAgentId: z.string().uuid().optional(),
    // Initial inputs available to all steps via $.inputs.<key>.
    inputs: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Manual retry of a terminated run ("resume" mode): flip the run back to
 * running and re-wake the assigned agent at the step it died on. Completed
 * steps keep their outputs — only the failed step is re-executed.
 *
 * Body is currently empty; it exists so a future `mode: "restart"` (fresh
 * run from the same inputs) can be added without a breaking change.
 */
export const retryWorkflowRunSchema = z.object({});
/**
 * Statuses a manual retry may be issued against.
 *
 * Deliberately excludes the live statuses. A "running" run is either
 * genuinely working or already covered by the sweeper (bounded step retry +
 * stall nudge), and an "awaiting" run is parked on an external event —
 * re-waking either would put two agents on the same run, and while the
 * runner's compare-and-swap prevents a double *advance*, both agents could
 * still perform the step's side effects before one loses the race.
 * "completed" is excluded because there is nothing to resume.
 */
export const WORKFLOW_RETRYABLE_RUN_STATUSES = [
    "failed",
    "cancelled",
];
/** Shared by the retry route guard and the UI's Retry button visibility. */
export function canRetryWorkflowRun(status) {
    return WORKFLOW_RETRYABLE_RUN_STATUSES.includes(status);
}
// --------------------------------------------------------------------
// Agent-facing: the workflow MCP tools the SKILL.md describes
// --------------------------------------------------------------------
/** get_workflow_run(runId) — no body, just :runId path param */
/** workflow_step_start — agent begins executing a step */
export const workflowStepStartSchema = z.object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
    // Optional: agent's view of inputs (echoed back for audit). The platform
    // computes the canonical resolved inputs from prior step outputs and
    // returns them in the response.
    inputs: z.record(z.string(), z.unknown()).optional(),
});
/** workflow_step_complete — step succeeded */
export const workflowStepCompleteSchema = z.object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
    outputs: z.record(z.string(), z.unknown()).default({}),
});
/** workflow_step_await — agent parks the step on an external event */
export const workflowStepAwaitSchema = z.object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
    awaitingType: z.enum(WORKFLOW_AWAIT_TYPES),
    // The id the resume hook will match against. For Teams cards this is the
    // activity_id (or proclawCorrelationId) of the card we just posted.
    correlationId: z.string().min(1).max(500),
    // Optional auto-timeout (ISO 8601). The node sets its own expiration; there
    // is no fixed cap. For Teams-card waits, when omitted we fall back to the
    // card's own expiry, then a platform fallback so the wait never hangs
    // forever. Other types have no timeout unless one is given.
    awaitingUntil: z.string().datetime().optional(),
    // Partial outputs to record before parking (e.g. "we posted card X").
    outputs: z.record(z.string(), z.unknown()).optional(),
});
/**
 * workflow_step_await_email_reply — park on a reply to a message previously
 * sent by the assigned agent's bound M365 mailbox.
 */
export const workflowStepAwaitEmailReplySchema = z
    .object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
    messageId: z.string().min(1).max(1000).optional(),
    conversationId: z.string().min(1).max(1000).optional(),
    internetMessageId: z.string().min(1).max(1000).optional(),
    expectedFrom: z.string().email(),
    sentAt: z.string().datetime(),
    waitDuration: z.number().int().positive().max(525_600).optional(),
    waitUnit: z.enum(["minutes", "hours", "days"]).optional(),
    // Backward compatibility for workflows saved before duration-based waits.
    deadlineAt: z.string().datetime().optional(),
    ignoredMessageIds: z.array(z.string().min(1).max(1000)).max(100).optional(),
})
    .refine((value) => Boolean(value.conversationId ||
    value.internetMessageId ||
    value.messageId), {
    message: "At least one of messageId, conversationId, or internetMessageId is required",
})
    .superRefine((value, ctx) => {
    const hasDuration = value.waitDuration !== undefined && value.waitUnit !== undefined;
    if (!value.deadlineAt && !hasDuration) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "waitDuration and waitUnit are required",
            path: ["waitDuration"],
        });
        return;
    }
    if (value.deadlineAt &&
        (value.waitDuration !== undefined || value.waitUnit !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Use either wait duration or legacy deadlineAt, not both",
            path: ["waitDuration"],
        });
        return;
    }
    const sentAtMs = new Date(value.sentAt).getTime();
    const unitMs = {
        minutes: 60_000,
        hours: 3_600_000,
        days: 86_400_000,
    };
    const deadlineMs = value.deadlineAt
        ? new Date(value.deadlineAt).getTime()
        : sentAtMs + value.waitDuration * unitMs[value.waitUnit];
    if (deadlineMs <= sentAtMs) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Reply deadline must be after sentAt",
            path: [value.deadlineAt ? "deadlineAt" : "waitDuration"],
        });
    }
    if (deadlineMs - sentAtMs > 365 * unitMs.days) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Email reply wait cannot exceed 365 days",
            path: ["waitDuration"],
        });
    }
});
/** workflow_step_fail — step failed */
export const workflowStepFailSchema = z.object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
    reason: z.string().min(1).max(2000),
    details: z.record(z.string(), z.unknown()).optional(),
});
/** workflow_run_fail — whole run unrecoverable */
export const workflowRunFailSchema = z.object({
    runId: z.string().uuid(),
    reason: z.string().min(1).max(2000),
});
/** evaluate_condition — platform decides the branch, agent obeys */
export const evaluateConditionSchema = z.object({
    runId: z.string().uuid(),
    stepId: z.string().min(1).max(200),
});
/**
 * workflow_post_message — post a message into a channel during a workflow
 * run. The platform attributes the post to the run's ASSIGNED agent (looked
 * up from runId), not the calling key — so channel posts always come from
 * the right agent even on shared OpenClaw VMs where mcporter key attribution
 * is unreliable. Use this instead of agent-comms send_message for channel
 * posts inside a workflow.
 */
export const workflowPostMessageSchema = z.object({
    runId: z.string().uuid(),
    channel: z.string().min(1).max(200),
    content: z.string().min(1).max(100000),
});
// --------------------------------------------------------------------
// Resume hook payload (called by external systems / Teams webhook)
// --------------------------------------------------------------------
/**
 * Inbound resume event. The platform matches `correlationId` against
 * workflow_runs.awaiting_correlation_id and wakes the assigned agent.
 * `eventOutputs` are merged into the step's outputs so the agent can
 * see what the external system reported (e.g. approve/reject from a
 * Teams card click).
 */
export const resumeWorkflowEventSchema = z.object({
    correlationId: z.string().min(1).max(500),
    eventOutputs: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=workflow-run.js.map